import {
  EMPTY_ARRAY,
  FormControlSizes,
  IconName,
  getAgGridColId,
  useDefaultColumns,
  type Column,
  type ColumnDef,
} from '@talos/kyoko';

import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useRollupTreeRef } from '@src/hooks/useRollupTree';
import type { IRowNode } from 'ag-grid-community';
import { useMemo } from 'react';
import { usePositionAutoHedgingRulesDrawer } from '../../../components/AutoHedgingRulesDrawer/AutoHedgingRulesDrawer';
import type { IHedgePositionStatusRow } from '../../../providers/HedgePositionStatusProvider';

export function useHedgePositionStatusColumns({
  size = 'full',
  defaultColumns,
}: {
  size: 'mini' | 'full';
  defaultColumns?: (string | Partial<Column<IHedgePositionStatusRow>>)[];
}) {
  const { enableOrderLinkInHedgeRule, enableHedgeRuleCrossAssetRiskGroup } = useFeatureFlag();
  const rollupTreeRef = useRollupTreeRef();

  const orderColumns = useMemo(
    () =>
      (enableOrderLinkInHedgeRule
        ? // If order link is enabled, show the columns rendering the order details
          [
            {
              initialHide: size === 'mini',
              field: 'HedgeOrderID',
              type: 'id',
              headerGroupToggle: true,
              // If toggling this group, order columns gets closed/opened
              columnsInGroup: ['Order.AvgPx', 'Order.CumQty', 'Order.Side', 'Order.Symbol', 'Order.Strategy'],
              columnGroup: true,
            },
            {
              title: 'Hedge Order AvgPx',
              initialHide: size === 'mini',
              columnGroup: true,
              field: 'Order.AvgPx',
              type: 'price',
              params: {
                assetField: 'Order.Symbol',
              },
            },
            {
              title: 'Hedge Order CumQty',
              initialHide: size === 'mini',
              columnGroup: true,
              field: 'Order.CumQty',
              type: 'size',
              params: {
                currencyField: 'Order.Currency',
              },
            },
            {
              title: 'Hedge Order Side',
              initialHide: size === 'mini',
              columnGroup: true,
              field: 'Order.Side',
              type: 'side',
            },
            {
              title: 'Hedge Order Symbol',
              initialHide: size === 'mini',
              columnGroup: true,
              field: 'Order.Symbol',
              type: 'security',
            },
            {
              title: 'Hedge Order Strategy',
              initialHide: size === 'mini',
              columnGroup: true,
              field: 'Order.Strategy',
              type: 'text',
            },
          ]
        : // If order link is disabled, show only the order id column
          [
            {
              initialHide: size === 'mini',
              field: 'HedgeOrderID',
              type: 'id',
            },
          ]) satisfies ColumnDef<IHedgePositionStatusRow>[],
    [enableOrderLinkInHedgeRule, size]
  );

  const { openPositionAutoHedgingRulesDrawer } = usePositionAutoHedgingRulesDrawer();
  const columnsDefs = useMemo(
    () =>
      new Map<string, Column>(
        (
          [
            {
              field: 'HedgeControlStatus',
              type: 'autohedgeStatus',
              title: 'Status',
              initialSort: '-',
              params: {
                textField: 'Text' satisfies keyof IHedgePositionStatusRow,
              },
            },
            {
              field: 'AccountID',
              title: 'Monitored Rollup',
              type: 'subAccountName',
              params: {
                rollupTree: rollupTreeRef,
                tooltipPlacement: 'bottom-end',
              },
              initialWidth: size === 'mini' ? 218 : undefined,
            },
            {
              id: 'current-asset-position',
              field: 'CurrentAssetPosition',
              title: size === 'mini' ? 'Current Asset Pos.' : 'Current Asset Position',
              initialWidth: size === 'mini' ? 127 : undefined,
              type: 'number',
              params: {
                currencyField: 'Asset',
              },
            },
            {
              initialHide: size === 'mini',
              id: 'current-position',
              field: 'CurrentPosition',
              type: 'number',
              params: {
                currencyField: 'TargetAsset',
              },
            },
            {
              id: 'target-position',
              field: 'TargetPosition',
              title: size === 'mini' ? 'Target Pos.' : 'Target Position',
              type: 'number',
              params: {
                currencyField: 'TargetAsset',
              },
              initialWidth: size === 'mini' ? 127 : undefined,
            },
            {
              title: 'Target Threshold',
              id: 'CurrentPosition',
              type: 'positionThresholdMeter',
              sortable: false,
            },
            {
              id: 'hedgeControls',
              type: 'button',
              frozen: true,
              initialPinned: 'right',
              initialWidth: size === 'full' ? 115 : 45,
              suppressColumnsToolPanel: true,
              params: {
                onClick: ({ node }: { node: IRowNode<IHedgePositionStatusRow> }) => {
                  if (!node.data) {
                    return;
                  }
                  openPositionAutoHedgingRulesDrawer({
                    hedgeRuleID: node.data.HedgeRuleID,
                  });
                },
                startIcon: IconName.Preferences,
                children: size === 'full' ? 'Edit Controls' : undefined,
                size: FormControlSizes.Small,
              },
            },
            ...orderColumns,
            { initialHide: size === 'mini', field: 'HedgeDecision', type: 'text' },
            { initialHide: size === 'mini', field: 'HedgeAction', type: 'text' },
            {
              initialHide: size === 'mini',
              field: 'ShortPositionLimit',
              type: 'number',
              params: { currencyField: 'TargetAsset' },
            },
            {
              initialHide: size === 'mini',
              field: 'LongPositionLimit',
              type: 'number',
              params: { currencyField: 'TargetAsset' },
            },

            { initialHide: true, field: 'HedgeSymbol', type: 'security' },
            { initialHide: true, field: 'HedgeRuleID', type: 'id', params: { maxLength: 6 } },
            { initialHide: true, field: 'Asset', title: 'Monitored Asset', type: 'currency' },
            { initialHide: true, field: 'TargetAsset', type: 'currency' },
            ...(enableHedgeRuleCrossAssetRiskGroup
              ? ([
                  {
                    initialHide: true,
                    field: 'CrossAssetRiskGroup',
                    title: 'Cross-Asset Risk Group',
                    type: 'text',
                  },
                ] satisfies ColumnDef<IHedgePositionStatusRow>[])
              : []),
            { initialHide: true, field: 'Timestamp', type: 'text' },
            { initialHide: true, field: 'LastLongAssetPositionDelta', type: 'text' },
            { initialHide: true, field: 'LastLongPositionDelta', type: 'text' },
            { initialHide: true, field: 'LongDeltaAttribution', type: 'text' },
            { initialHide: true, field: 'LastShortAssetPositionDelta', type: 'text' },
            { initialHide: true, field: 'LastShortPositionDelta', type: 'text' },
            { initialHide: true, field: 'ShortDeltaAttribution', type: 'text' },
            { initialHide: true, field: 'HedgeDecisionData', type: 'text' },
          ] satisfies ColumnDef<IHedgePositionStatusRow>[]
        ).map(c => [getAgGridColId(c), c])
      ),
    [rollupTreeRef, size, orderColumns, openPositionAutoHedgingRulesDrawer, enableHedgeRuleCrossAssetRiskGroup]
  );

  return useDefaultColumns(defaultColumns ?? EMPTY_ARRAY, columnsDefs);
}
